import React, { useEffect, useState } from "react";
import ApexCharts from "apexcharts";
import api from "../../../../../api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CasesRespondedBarChart = ({ month, year, onSummaryUpdate, setExportFunctions }) => {
  const [chartData, setChartData] = useState({ labels: [], data: [] });
  const [noData, setNoData] = useState(false);
  const [chartInstance, setChartInstance] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/ems/emergency-case-responded", {
          params: { month, year },
        });

        const { today_cases, this_week_cases, this_month_cases, weekly_cases } = response.data;

        onSummaryUpdate({
          today: today_cases,
          week: this_week_cases,
          month: this_month_cases,
        });

        if (weekly_cases?.total_cases && weekly_cases.total_cases.length > 0) {
          setChartData({
            labels: weekly_cases.week_labels,
            data: weekly_cases.total_cases,
          });
          const allZero = weekly_cases.total_cases.every((v) => v === 0);
          setNoData(allZero);
        } else {
          setChartData({ labels: [], data: [] });
          setNoData(true);
        }
      } catch (error) {
        console.error("Error fetching cases responded data:", error);
        setChartData({ labels: [], data: [] });
        setNoData(true);
        onSummaryUpdate({ today: 0, week: 0, month: 0 });
      }
    };

    fetchData();
  }, [month, year, onSummaryUpdate]);

  const exportPDF = async () => {
    try {
      const dataURI = await ApexCharts.exec("cases-responded", "dataURI", {
        scale: 2,
        width: 800,
      });

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const monthName = months[month - 1];

      // Add chart image
      const chartHeight = 100;
      const chartTopMargin = 40;
      doc.addImage(dataURI.imgURI, "PNG", 10, chartTopMargin, 190, chartHeight);

      // Define table options
      const options = {
        startY: chartTopMargin + chartHeight + 20,
        head: [["Week", "Cases Responded"]],
        body: chartData.labels.map((label, index) => [
          label,
          chartData.data[index]?.toFixed(0) || "0",
        ]),
        theme: "grid",
        headStyles: {
          fillColor: [0, 123, 255], // Blue header (#007bff)
          textColor: 255,
          fontStyle: "bold",
        },
        styles: {
          fontSize: 10,
          cellPadding: 3,
        },
        margin: { left: 10, right: 10 },
        didDrawPage: (data) => {
          // Header - Main title
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text("Emergency Cases Responded Report", pageWidth / 2, 20, {
            align: "center",
          });

          // Header - Period information
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text(`Period: ${monthName} ${year}`, pageWidth / 2, 30, {
            align: "center",
          });

          // Footer - Page number
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: "center" }
          );
        },
      };

      // Add table only if data exists
      if (chartData.labels.length > 0 && !noData) {
        autoTable(doc, options);
      } else {
        // Add message if no data
        doc.setFontSize(10);
        doc.text(
          "No data available for the selected period.",
          pageWidth / 2,
          chartTopMargin + chartHeight + 20,
          { align: "center" }
        );
      }

      doc.save(`cases_responded_${year}_${month}.pdf`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
    }
  };

  const exportXML = () => {
    let xmlData = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xmlData += `<EmergencyCasesResponded month="${month}" year="${year}">\n`;
    
    if (chartData.labels.length > 0 && !noData) {
      chartData.labels.forEach((label, index) => {
        xmlData += `  <Week>\n`;
        xmlData += `    <Label>${label}</Label>\n`;
        xmlData += `    <Cases>${chartData.data[index]?.toFixed(0) || '0'}</Cases>\n`;
        xmlData += `  </Week>\n`;
      });
    } else {
      xmlData += `  <Message>No data available for the selected period.</Message>\n`;
    }
    xmlData += `</EmergencyCasesResponded>`;

    const blob = new Blob([xmlData], { type: "application/xml" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `cases_responded_${year}_${month}.xml`;
    link.click();
  };

  const downloadGraph = async () => {
    try {
      const dataURI = await ApexCharts.exec("cases-responded", "dataURI", {
        scale: 2,
        width: 1200,
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height + 50;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = '#000000';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Emergency Cases Responded', canvas.width / 2, 30);

        const monthName = months[month - 1];
        ctx.font = '16px Arial';
        ctx.fillText(`Period: ${monthName} ${year}`, canvas.width / 2, 55);

        ctx.drawImage(img, 0, 60, img.width, img.height);

        const link = document.createElement('a');
        link.download = `cases_responded_${year}_${month}.png`;
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      img.src = dataURI.imgURI;
    } catch (error) {
      console.error("Error downloading graph:", error);
    }
  };

  useEffect(() => {
    setExportFunctions({ exportPDF, exportXML, downloadGraph });
  }, [setExportFunctions, chartData, chartInstance]);

  useEffect(() => {
    const chartContainer = document.querySelector("#cases-responded");

    if (chartContainer) {
      const maxDataValue = Math.max(...chartData.data, 10);
      const maxYAxisValue = Math.ceil(maxDataValue / 5) * 5;

      const options = {
        series: [{ name: "Cases Responded", data: chartData.data }],
        chart: {
          id: "cases-responded",
          height: 265,
          type: "bar",
          fontFamily: "Plus Jakarta Sans, sans-serif",
          foreColor: "#adb0bb",
          toolbar: {
            show: false,
            export: {
              png: {
                filename: `cases_responded_${year}_${month}`,
              },
            },
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          },
          events: {
            mounted: function(chartContext, config) {
              setChartInstance(chartContext);
            },
            updated: function(chartContext, config) {
              setChartInstance(chartContext);
            },
          },
        },
        colors: ["#007bff"], // Set bar color to blue
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "40%",
            borderRadiusApplication: "end",
            dataLabels: {
              position: 'top',
            },
          },
        },
        stroke: {
          show: false,
          width: 2,
          colors: ['transparent'],
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val ? val.toFixed(0) : '0';
          },
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ["#304758"],
          },
        },
        legend: { show: false },
        grid: {
          show: false,
          borderColor: '#e0e0e0',
          strokeDashArray: 3,
        },
        yaxis: {
          min: 0,
          max: maxYAxisValue,
          tickAmount: 4,
          labels: {
            formatter: (val) => val.toFixed(0),
            style: {
              colors: '#6b7280',
              fontSize: '12px',
            },
          },
          title: {
            text: 'Cases Responded',
            style: {
              color: '#6b7280',
              fontSize: '12px',
            },
          },
        },
        xaxis: {
          categories: chartData.labels,
          axisTicks: { show: false },
          labels: {
            style: {
              colors: '#6b7280',
              fontSize: '12px',
            },
          },
        },
        tooltip: {
          theme: "dark",
          x: { show: true },
          y: {
            formatter: function(val) {
              return val ? `${val.toFixed(0)} cases` : '0 cases';
            },
          },
          style: {
            fontSize: '12px',
          },
        },
        states: {
          hover: {
            filter: {
              type: 'darken',
              value: 0.8,
            },
          },
        },
      };

      const chart = new ApexCharts(chartContainer, options);
      chart.render();
      setChartInstance(chart);

      return () => {
        chart.destroy();
        setChartInstance(null);
      };
    }
  }, [chartData, month, year]);

  return (
    <>
      <div id="cases-responded"></div>
      {noData && (
        <div className="text-center mt-3 fw-semibold text-muted">
          Data is Not Available
        </div>
      )}
    </>
  );
};

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

const CasesRespondedCard = () => {
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const [summary, setSummary] = useState({
    today: 0,
    week: 0,
    month: 0,
  });

  const [exportFunctions, setExportFunctions] = useState({
    exportPDF: () => {},
    exportXML: () => {},
    downloadGraph: () => {},
  });

  return (
    <>
      <style>
        {`
          @media(max-width: 1399px) {
            .analytic-card .top-wrap {flex-wrap: wrap;}
            .analytic-card .top-wrap .card-title{width: 100%;}
            .analytic-card .top-wrap .select-panel {margin: 1rem 0;}
          }
        `}
      </style>
      
      <div className="card w-100 analytic-card position-relative overflow-hidden mb-0 h-100">
        <div className="card-body">
          <div className="top-wrap mb-4">
            <h5 className="card-title mb-0 fw-semibold">
              Emergency Cases Responded
            </h5>
            <div className="select-panel d-flex gap-2 w-100 mt-3">
              <select
                className="form-select text-dark"
                value={filters.month}
                onChange={(e) =>
                  setFilters({ ...filters, month: Number(e.target.value) })
                }
              >
                {months.map((m, i) => (
                  <option key={i + 1} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                className="form-select text-dark"
                value={filters.year}
                onChange={(e) =>
                  setFilters({ ...filters, year: Number(e.target.value) })
                }
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <div className="dropdown">
                <button
                  className="btn btn-outline-primary dropdown-toggle"
                  type="button"
                  id="exportDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Export Options
                </button>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="exportDropdown">
                  <li>
                    <button className="dropdown-item" onClick={exportFunctions.exportPDF}>
                      Export PDF
                    </button>
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={exportFunctions.exportXML}>
                      Export XML
                    </button>
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={exportFunctions.downloadGraph}>
                      Download Graph
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <CasesRespondedBarChart
            month={filters.month}
            year={filters.year}
            onSummaryUpdate={setSummary}
            setExportFunctions={setExportFunctions}
          />

          <div className="d-flex align-items-center justify-content-center flex-wrap mt-3">
            <div className="d-flex align-items-center justify-content-center p-2">
              <span className="round-8 text-bg-primary rounded-circle me-2 d-inline-block"></span>
              <h6 className="mb-0">
                <strong>Today:</strong>{" "}
                <span className="text-muted">{summary.today} Cases</span>
              </h6>
            </div>
            <div className="d-flex align-items-center justify-content-center p-2">
              <span className="round-8 text-bg-primary rounded-circle me-2 d-inline-block"></span>
              <h6 className="mb-0">
                <strong>This Week:</strong>{" "}
                <span className="text-muted">{summary.week} Cases</span>
              </h6>
            </div>
            <div className="d-flex align-items-center justify-content-center p-2">
              <span className="round-8 text-bg-primary rounded-circle me-2 d-inline-block"></span>
              <h6 className="mb-0">
                <strong>This Month:</strong>{" "}
                <span className="text-muted">{summary.month} Cases</span>
              </h6>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CasesRespondedCard;